import React, { useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { Modal, ModalBody } from "reactstrap"
import { useFormik } from "formik"
import * as yup from "yup"
import { toast, Bounce } from "react-toastify"
import { useSelector } from "react-redux"
import Select from "react-select"
import { components as selectComponents } from "react-select"
import CustomInput from "../OnboardingComponents/CustomInput"
import { DateField } from "../OnboardingComponents/CustomDatePicker"
import CustomCheckbox from "../OnboardingComponents/CustomCheckbox"
import CustomButton from "../OnboardingComponents/CustomButton"
import { Avatar } from "../DashboardComponents/AvatarGroup"
import { ICONS } from "../OnboardingComponents/icon"
import { ICONS as APP_ICONS } from "../../constants/icons"
import { post } from "../../api/axios"
import endpoints from "../../api/endpoints"
import { toastApiError } from "../../helpers/apiError"
import { getUserAvatarUrl } from "../../helpers/userAvatar"
import { withYouLabel } from "../../helpers/userDisplay"

const DAYS = [
  { key: "monday", label: "Mon", full: "Monday" },
  { key: "tuesday", label: "Tue", full: "Tuesday" },
  { key: "wednesday", label: "Wed", full: "Wednesday" },
  { key: "thursday", label: "Thu", full: "Thursday" },
  { key: "friday", label: "Fri", full: "Friday" },
  { key: "saturday", label: "Sat", full: "Saturday" },
  { key: "sunday", label: "Sun", full: "Sunday" },
]

const WEEKDAY_BY_INDEX = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
]

const toDateOnly = value => {
  if (!value) return ""
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/)
  return match ? match[1] : ""
}

/** Local calendar date as YYYY-MM-DD (avoids UTC off-by-one from toISOString). */
const todayIso = () => {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

/** Later of two YYYY-MM-DD strings; empty values are ignored. */
const laterDate = (a, b) => {
  const left = toDateOnly(a)
  const right = toDateOnly(b)
  if (!left) return right
  if (!right) return left
  return left >= right ? left : right
}

const weekdayKeyFromIsoDate = isoDate => {
  const dateOnly = toDateOnly(isoDate)
  if (!dateOnly) return null
  const [year, month, day] = dateOnly.split("-").map(Number)
  if (!year || !month || !day) return null
  return WEEKDAY_BY_INDEX[new Date(year, month - 1, day).getDay()] || null
}

const parseLocalDate = isoDate => {
  const dateOnly = toDateOnly(isoDate)
  if (!dateOnly) return null
  const [year, month, day] = dateOnly.split("-").map(Number)
  if (!year || !month || !day) return null
  return new Date(year, month - 1, day)
}

/** Weekday keys that occur at least once between start and end (inclusive). */
const weekdaysInRange = (startIso, endIso) => {
  const start = parseLocalDate(startIso)
  const end = parseLocalDate(endIso || startIso)
  if (!start || !end || end < start) return new Set()

  const set = new Set()
  const cursor = new Date(start)
  while (cursor <= end) {
    set.add(WEEKDAY_BY_INDEX[cursor.getDay()])
    if (set.size === 7) break
    cursor.setDate(cursor.getDate() + 1)
  }
  return set
}

const isTimeString = value => typeof value === "string" && value.length === 5

/** Format HH:mm (24h) as 12-hour label, e.g. "08:00" → "8:00 AM". */
const formatTime12Hour = value => {
  if (!isTimeString(value)) return value || ""
  const [hourStr, minuteStr] = value.split(":")
  const hour24 = Number(hourStr)
  const period = hour24 >= 12 ? "PM" : "AM"
  const hour12 = hour24 % 12 || 12
  return `${hour12}:${minuteStr} ${period}`
}

/** Decimal hours from HH:mm start/end (0 when invalid or end ≤ start). */
const hoursFromTimes = (startTime, endTime) => {
  if (!isTimeString(startTime) || !isTimeString(endTime)) return 0
  const [startHour, startMinute] = startTime.split(":").map(Number)
  const [endHour, endMinute] = endTime.split(":").map(Number)
  const diffMinutes = endHour * 60 + endMinute - (startHour * 60 + startMinute)
  if (diffMinutes <= 0) return 0
  return Math.round((diffMinutes / 60) * 100) / 100
}

const TIME_STEP_MINUTES = 15
const MIN_SCHEDULE_HOURS = 0.5
const DEFAULT_START_TIME = "08:00"
const DEFAULT_END_TIME = "17:00"

const TIME_OPTIONS = (() => {
  const options = []
  for (let minutes = 0; minutes < 24 * 60; minutes += TIME_STEP_MINUTES) {
    const hh = String(Math.floor(minutes / 60)).padStart(2, "0")
    const mm = String(minutes % 60).padStart(2, "0")
    const value = `${hh}:${mm}`
    options.push({ value, label: formatTime12Hour(value) })
  }
  return options
})()

const resolveTimeOption = value => {
  if (!isTimeString(value)) return null
  return (
    TIME_OPTIONS.find(option => option.value === value) || {
      value,
      label: formatTime12Hour(value),
    }
  )
}

/** First end time that is after start and meets the minimum hours. */
const firstValidEndAfter = startTime => {
  if (!isTimeString(startTime)) return ""
  const match = TIME_OPTIONS.find(
    option => hoursFromTimes(startTime, option.value) >= MIN_SCHEDULE_HOURS,
  )
  return match?.value || ""
}

const endTimeOptionsAfter = startTime => {
  if (!isTimeString(startTime)) return TIME_OPTIONS
  return TIME_OPTIONS.filter(
    option => hoursFromTimes(startTime, option.value) >= MIN_SCHEDULE_HOURS,
  )
}

const scheduleTimeSelectStyles = (hasError = false) => ({
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    height: 38,
    border: hasError ? "1px solid #9b0000" : "none",
    borderRadius: 12,
    backgroundColor: "#f3f3f5",
    boxShadow: "none",
    cursor: "pointer",
    opacity: state.isFocused ? 1 : 0.85,
    "&:hover": { borderColor: hasError ? "#9b0000" : "transparent" },
  }),
  valueContainer: base => ({
    ...base,
    padding: "0 8px",
    justifyContent: "flex-end",
  }),
  singleValue: base => ({
    ...base,
    margin: 0,
    color: "#0d0d0d",
    fontSize: 14,
    fontWeight: 500,
    textAlign: "right",
  }),
  input: base => ({
    ...base,
    margin: 0,
    color: "#0d0d0d",
    fontSize: 14,
  }),
  placeholder: base => ({
    ...base,
    margin: 0,
    color: "rgba(110, 110, 110, 0.7)",
    fontSize: 14,
  }),
  indicatorsContainer: base => ({
    ...base,
    height: 38,
  }),
  dropdownIndicator: base => ({
    ...base,
    padding: "0 6px 0 0",
    color: "#6e6e6e",
  }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: base => ({
    ...base,
    marginTop: 4,
    borderRadius: 12,
    boxShadow: "0 8px 24px rgba(17, 17, 17, 0.12)",
    overflow: "hidden",
    zIndex: 10060,
  }),
  menuPortal: base => ({ ...base, zIndex: 10060 }),
  menuList: base => ({
    ...base,
    maxHeight: 220,
    padding: 4,
    overflowY: "auto",
    overscrollBehavior: "contain",
    WebkitOverflowScrolling: "touch",
  }),
  option: (base, state) => ({
    ...base,
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 500,
    backgroundColor: state.isSelected
      ? "#ecf0fa"
      : state.isFocused
        ? "rgba(13, 13, 13, 0.04)"
        : "transparent",
    color: "#0d0d0d",
    cursor: "pointer",
  }),
})

const ScheduleTimeSelect = ({
  value,
  onChange,
  ariaLabel,
  hasError = false,
  id,
  options = TIME_OPTIONS,
}) => (
  <Select
    inputId={id}
    className="amok-modal__time-select"
    classNamePrefix="amok-modal-time"
    options={options}
    value={resolveTimeOption(value)}
    onChange={option => onChange(option?.value || "")}
    isSearchable
    menuPlacement="auto"
    menuPosition="fixed"
    menuShouldScrollIntoView
    maxMenuHeight={220}
    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
    styles={scheduleTimeSelectStyles(hasError)}
    aria-label={ariaLabel}
    placeholder="8:00 AM"
    components={{ IndicatorSeparator: null }}
  />
)

const DEFAULT_WORKING_DAY = {
  startTime: DEFAULT_START_TIME,
  endTime: DEFAULT_END_TIME,
  hours: hoursFromTimes(DEFAULT_START_TIME, DEFAULT_END_TIME),
}

const INITIAL_VALUES = {
  title: "",
  startDate: "",
  endDate: "",
  collaborators: [],
  description: "",
  workingDays: [],
}

const optionMatchesUser = (option, userId) => {
  if (!userId || !option) return false
  // Prefer explicit userId so option.value can safely be the project member id.
  if (option.userId) {
    return String(option.userId) === String(userId)
  }
  return String(option.value || "") === String(userId)
}

const mapCurrentUserToOption = (user, memberId = null) => {
  if (!user?.id || !memberId) return null
  const name =
    `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
    user.email ||
    "You"
  const email = user.email || ""
  const userId = String(user.id)

  return {
    value: String(memberId),
    label: email ? `${name} (${email})` : name,
    name: withYouLabel(name),
    email,
    avatar: getUserAvatarUrl(user),
    userId,
    isFixed: true,
  }
}

const lockCurrentUserOption = (option, currentUserId) => {
  if (!optionMatchesUser(option, currentUserId)) return option
  return {
    ...option,
    isFixed: true,
    name: withYouLabel(option.name),
  }
}

const ensureCurrentUserSelected = (
  selected,
  currentUserOption,
  currentUserId,
) => {
  const next = (selected || []).filter(Boolean)
  if (!currentUserOption) return next
  const others = next.filter(
    option => !optionMatchesUser(option, currentUserId),
  )
  return [currentUserOption, ...others]
}

const mapMemberToOption = member => {
  const user = member?.user || {}
  const name =
    member?.name ||
    `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
    user.full_name ||
    user.email ||
    member?.email ||
    "Collaborator"
  const email = user.email || member?.email || ""
  // Project member row id — required for collaborator_ids (not auth user id).
  const memberId = member?.id
  const userId =
    member?.userId || member?.user_id || user.id || member?.user?.id || ""

  if (!memberId) return null

  return {
    value: String(memberId),
    label: email ? `${name} (${email})` : name,
    name,
    email,
    avatar: member?.avatar || getUserAvatarUrl(user),
    userId: userId ? String(userId) : "",
  }
}

const CollaboratorOption = props => {
  const { data } = props
  return (
    <selectComponents.Option {...props}>
      <span className="project-collab-modal__option">
        <Avatar
          src={data.avatar}
          name={data.name}
          size="xs"
          className="project-collab-modal__option-avatar"
        />
        <span className="project-collab-modal__option-meta">
          <span className="project-collab-modal__option-name">{data.name}</span>
          {data.email ? (
            <span className="project-collab-modal__option-email">{data.email}</span>
          ) : null}
        </span>
      </span>
    </selectComponents.Option>
  )
}

const CollaboratorMultiValueLabel = props => {
  const { data } = props
  const chipKey = data?.value || data?.userId || data?.email || data?.name
  return (
    <selectComponents.MultiValueLabel {...props}>
      <span className="project-collab-modal__chip">
        <Avatar
          key={chipKey}
          src={data.avatar}
          name={data.name}
          size="xs"
          className="project-collab-modal__chip-avatar"
        />
        <span className="project-collab-modal__chip-name">{data.name}</span>
      </span>
    </selectComponents.MultiValueLabel>
  )
}

const collaboratorSelectStyles = (hasError = false) => ({
  control: (base, state) => ({
    ...base,
    minHeight: 30,
    border: "none",
    borderRadius: 12,
    backgroundColor: "transparent",
    boxShadow: "none",
    cursor: "text",
    paddingLeft: 0,
    borderColor: hasError ? "#9b0000" : "transparent",
    "&:hover": { border: "none", backgroundColor: "transparent" },
    ...(state.isFocused ? { outline: "none" } : {}),
  }),
  valueContainer: base => ({
    ...base,
    padding: "0",
    gap: 6,
    minHeight: 30,
  }),
  container: base => ({
    ...base,
    width: "100%",
  }),
  input: base => ({
    ...base,
    margin: 0,
    padding: 0,
    color: "#0d0d0d",
    fontSize: 14,
    fontWeight: 500,
  }),
  placeholder: base => ({
    ...base,
    margin: 0,
    color: "rgba(110, 110, 110, 0.7)",
    fontSize: 14,
    fontWeight: 500,
  }),
  singleValue: base => ({
    ...base,
    margin: 0,
    color: "#0d0d0d",
    fontSize: 14,
    fontWeight: 500,
  }),
  multiValue: base => ({
    ...base,
    margin: 0,
    backgroundColor: "#ecf0fa",
    borderRadius: 999,
  }),
  multiValueLabel: base => ({
    ...base,
    padding: "2px 4px 2px 2px",
  }),
  multiValueRemove: (base, state) => ({
    ...base,
    ...(state.data?.isFixed ? { display: "none" } : {}),
    borderRadius: 999,
    color: "#6e6e6e",
    cursor: "pointer",
    ":hover": {
      backgroundColor: "rgba(155, 0, 0, 0.08)",
      color: "#9b0000",
    },
  }),
  clearIndicator: base => ({
    ...base,
    padding: 4,
  }),
  dropdownIndicator: () => ({ display: "none" }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: base => ({
    ...base,
    marginTop: 8,
    borderRadius: 12,
    boxShadow: "0 8px 24px rgba(17, 17, 17, 0.08)",
    overflow: "hidden",
    zIndex: 10060,
  }),
  menuPortal: base => ({ ...base, zIndex: 10060 }),
  menuList: base => ({
    ...base,
    padding: 4,
    maxHeight: 220,
  }),
  option: (base, state) => ({
    ...base,
    padding: "8px 10px",
    borderRadius: 8,
    backgroundColor:
      state.isFocused || state.isSelected ? "#f2f2f4" : "transparent",
    color: "#0d0d0d",
    cursor: "pointer",
    "&:active": { backgroundColor: "#e7e7ea" },
  }),
  noOptionsMessage: base => ({
    ...base,
    fontSize: 13,
    color: "#b7b7b7",
  }),
})

const isWithinProjectRange = (value, projectStart, projectEnd) => {
  if (!value) return true
  if (projectStart && value < projectStart) return false
  if (projectEnd && value > projectEnd) return false
  return true
}

const projectRangeMessage = (projectStart, projectEnd) => {
  if (projectStart && projectEnd) {
    return `Date must be on or between ${projectStart} and ${projectEnd}`
  }
  if (projectStart) {
    return `Date must be on or after ${projectStart}`
  }
  return "Date is outside the project range"
}

const buildValidationSchema = (projectStartDate, projectEndDate) => {
  const projectStart = toDateOnly(projectStartDate)
  const projectEnd = toDateOnly(projectEndDate)

  return yup.object().shape({
    title: yup
      .string()
      .trim()
      .required("Task title is required")
      .min(2, "Task title must be at least 2 characters")
      .max(120, "Task title must be less than 120 characters"),
    startDate: yup
      .string()
      .required("Start date is required")
      .test(
        "not-in-past",
        "Start date must be today or later",
        value => {
          if (!value) return true
          return value >= todayIso()
        },
      )
      .test(
        "within-project",
        projectRangeMessage(projectStart, projectEnd),
        value => isWithinProjectRange(value, projectStart, projectEnd),
      )
      .test(
        "on-or-after-earliest",
        function (value) {
          if (!value) return true
          const earliestStart = laterDate(todayIso(), projectStart)
          if (!earliestStart || value >= earliestStart) return true
          return this.createError({
            message: `Start date must be on or after ${earliestStart}`,
          })
        },
      ),
    endDate: yup
      .string()
      .required("End date is required")
      .test(
        "is-after-start",
        "End date must be on or after start date",
        function (value) {
          const { startDate } = this.parent
          if (!startDate || !value) return true
          return value >= startDate
        },
      )
      .test(
        "within-project",
        projectRangeMessage(projectStart, projectEnd),
        value => {
          // No project end date → skip upper-bound validation on end date
          if (!projectEnd) {
            if (projectStart && value && value < projectStart) return false
            return true
          }
          return isWithinProjectRange(value, projectStart, projectEnd)
        },
      ),
    collaborators: yup.array().of(yup.object()).nullable(),
    description: yup.string().trim().max(500, "Description must be 500 characters or less"),
    workingDays: yup
      .array()
      .of(
        yup.object().shape({
          day: yup.string().required(),
          startTime: yup
            .string()
            .nullable()
            .test(
              "valid-start-time",
              "Start time must be a valid time",
              value => !value || isTimeString(value),
            ),
          endTime: yup
            .string()
            .nullable()
            .test(
              "valid-end-time",
              "End time must be a valid time",
              value => !value || isTimeString(value),
            )
            .test(
              "is-after-start-time",
              "End time must be after start time",
              function (value) {
                const { startTime } = this.parent
                if (!isTimeString(startTime) || !isTimeString(value)) return true
                return hoursFromTimes(startTime, value) >= MIN_SCHEDULE_HOURS
              },
            ),
          hours: yup
            .number()
            .transform((value, originalValue) =>
              originalValue === "" ||
              originalValue === null ||
              originalValue === undefined
                ? undefined
                : Number(originalValue),
            )
            .nullable()
            .notRequired()
            .typeError("Hours must be a number")
            .min(MIN_SCHEDULE_HOURS, "Hours must be at least 0.5"),
        }),
      ),
  })
}

const dayFull = key => DAYS.find(d => d.key === key)?.full || key

/**
 * SetupWorkingScheduleModal
 * ------------------------------------------------------------------
 * Modal that lets the user define a working schedule and create a
 * project task via the API. Built on Formik + Yup with inline
 * validation, loading states and toast feedback.
 */
const SetupWorkingScheduleModal = ({
  isOpen,
  onClose,
  onSave = null,
  projectId = "",
  lockedDate = "",
  projectStartDate = "",
  projectEndDate = "",
  project = null,
}) => {
  const currentUser = useSelector(state => state.auth?.currentUser)
  const currentUserId = currentUser?.id != null ? String(currentUser.id) : ""
  const [collabMenuOpen, setCollabMenuOpen] = useState(false)
  const isDateLocked = Boolean(lockedDate)
  const lockedDayKey = isDateLocked ? weekdayKeyFromIsoDate(lockedDate) : null
  const projectStart = toDateOnly(projectStartDate)
  const projectEnd = toDateOnly(projectEndDate)

  const { collaboratorOptions, currentUserOption } = useMemo(() => {
    const list =
      Array.isArray(project?.members) && project.members.length > 0
        ? project.members
        : project?.collaborators || []

    const options = list
      .map(mapMemberToOption)
      .filter(Boolean)
      .filter(option => option.value)
      .map(option => lockCurrentUserOption(option, currentUserId))

    const fromList = options.find(option =>
      optionMatchesUser(option, currentUserId),
    )

    // Always use project member id as value; enrich label/avatar from auth user
    // when create-project members have user_id but no nested user.
    let current = fromList
    if (currentUserId) {
      const rawMember = list.find(member => {
        const memberUserId =
          member?.userId ||
          member?.user_id ||
          member?.user?.id ||
          ""
        return String(memberUserId) === currentUserId && member?.id
      })
      if (rawMember?.id) {
        current = mapCurrentUserToOption(currentUser, rawMember.id) || fromList
      }
    }

    if (
      current &&
      !options.some(option => optionMatchesUser(option, currentUserId))
    ) {
      options.unshift(current)
    } else if (current) {
      const idx = options.findIndex(option =>
        optionMatchesUser(option, currentUserId),
      )
      if (idx >= 0) options[idx] = { ...options[idx], ...current, isFixed: true }
    }

    return { collaboratorOptions: options, currentUserOption: current }
  }, [project, currentUser, currentUserId])

  const validationSchema = useMemo(
    () => buildValidationSchema(projectStart, projectEnd),
    [projectStart, projectEnd],
  )

  const formik = useFormik({
    initialValues: INITIAL_VALUES,
    validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values, { setSubmitting }) => {
      if (!projectId) {
        toast.error("Project ID is missing. Please create the project first.", {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        })
        setSubmitting(false)
        return
      }

      const payload = {
        title: values.title.trim(),
        start_date: values.startDate,
        end_date: values.endDate,
        working_days: values.workingDays.map(day => {
          const startTime = day.startTime
          const endTime = day.endTime
          return {
            day: day.day,
            start_time: startTime,
            end_time: endTime,
            // Always derive hours from times so API matches the schedule window
            hours: hoursFromTimes(startTime, endTime),
          }
        }),
      }

      const description = String(values.description || "").trim()
      if (description) {
        payload.description = description
      }

      const collaboratorIds = Array.from(
        new Set(
          ensureCurrentUserSelected(
            values.collaborators,
            currentUserOption,
            currentUserId,
          )
            .map(item => String(item.value || "").trim())
            .filter(Boolean),
        ),
      )
      if (collaboratorIds.length > 0) {
        payload.collaborator_ids = collaboratorIds
      }

      try {
        const response = await post(endpoints.createTask(projectId), payload)

        if (response?.data?.success) {
          toast.success(
            response?.data?.message || "Task created successfully",
            {
              position: "bottom-center",
              theme: "colored",
              transition: Bounce,
            },
          )
          onSave?.(response?.data?.result)
        } else {
          toastApiError(response, "Failed to create task")
        }
      } catch (error) {
        toastApiError(error, "Failed to create task. Please try again.")
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (!isOpen) return

    if (lockedDate && lockedDayKey) {
      const locked = toDateOnly(lockedDate)
      const today = todayIso()
      if (locked && locked < today) {
        toast.error("You can only schedule tasks for today or a future date.", {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        })
        onClose?.()
        return
      }

      formik.resetForm({
        values: {
          ...INITIAL_VALUES,
          startDate: locked,
          endDate: locked,
          workingDays: [{ day: lockedDayKey, ...DEFAULT_WORKING_DAY }],
          collaborators: currentUserOption ? [currentUserOption] : [],
        },
      })
      return
    }

    formik.resetForm({
      values: {
        ...INITIAL_VALUES,
        collaborators: currentUserOption ? [currentUserOption] : [],
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, lockedDate, lockedDayKey])

  useEffect(() => {
    if (!isOpen || !currentUserOption) return
    const selected = formik.values.collaborators || []
    if (selected.some(option => optionMatchesUser(option, currentUserId))) return
    formik.setFieldValue(
      "collaborators",
      ensureCurrentUserSelected(selected, currentUserOption, currentUserId),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentUserOption, currentUserId])

  const allowedWeekdays = useMemo(
    () => weekdaysInRange(formik.values.startDate, formik.values.endDate),
    [formik.values.startDate, formik.values.endDate],
  )

  // Drop selected working days when dates are incomplete or out of range
  useEffect(() => {
    if (isDateLocked) return

    if (!formik.values.startDate || !formik.values.endDate) {
      if (formik.values.workingDays.length > 0) {
        formik.setFieldValue("workingDays", [])
      }
      return
    }

    const next = formik.values.workingDays.filter(d =>
      allowedWeekdays.has(d.day),
    )
    if (next.length !== formik.values.workingDays.length) {
      formik.setFieldValue("workingDays", next)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allowedWeekdays,
    formik.values.startDate,
    formik.values.endDate,
    isDateLocked,
  ])

  const selectedKeys = useMemo(
    () => formik.values.workingDays.map(d => d.day),
    [formik.values.workingDays],
  )

  const datePickerMin = laterDate(todayIso(), projectStart) || undefined
  const datePickerMax = projectEnd || undefined

  const toggleDay = key => {
    if (isDateLocked) return
    if (!allowedWeekdays.has(key)) return

    const already = formik.values.workingDays.find(d => d.day === key)
    const next = already
      ? formik.values.workingDays.filter(d => d.day !== key)
      : [...formik.values.workingDays, { day: key, ...DEFAULT_WORKING_DAY }]
    formik.setFieldValue("workingDays", next)
    formik.setFieldTouched("workingDays", true, false)
  }

  const updateWorkingDay = (day, field, value) => {
    formik.setFieldValue(
      "workingDays",
      formik.values.workingDays.map(d => {
        if (d.day !== day) return d
        const next = { ...d, [field]: value }

        if (field === "startTime") {
          let endTime = d.endTime
          if (
            !isTimeString(endTime) ||
            hoursFromTimes(value, endTime) < MIN_SCHEDULE_HOURS
          ) {
            endTime = firstValidEndAfter(value)
          }
          next.endTime = endTime
          const hours = hoursFromTimes(value, endTime)
          next.hours = hours > 0 ? hours : ""
        } else if (field === "endTime") {
          // Reject end times that are not strictly after start (keep previous)
          if (
            isTimeString(d.startTime) &&
            hoursFromTimes(d.startTime, value) < MIN_SCHEDULE_HOURS
          ) {
            return d
          }
          const hours = hoursFromTimes(d.startTime, value)
          next.hours = hours > 0 ? hours : ""
        }

        return next
      }),
    )
  }

  const hasScheduleDates = Boolean(
    formik.values.startDate && formik.values.endDate,
  )

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      className="amok-modal amok-modal--schedule"
      contentClassName="amok-modal__content"
      backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
    >
      <ModalBody className="amok-modal__body">
        <form onSubmit={formik.handleSubmit} className="amok-modal__shell">
          <div className="amok-modal__main">
            <div className="amok-modal__header">
              <h2 className="amok-modal__title amok-modal__title--left">
                Setup Working Schedule
              </h2>
              <p className="amok-modal__subtitle amok-modal__subtitle--left">
                Define your working schedule by selecting working days and
                assigning hours for each selected day.
              </p>
            </div>

            <div className="amok-modal__form">
            <div className="amok-modal__section">
              <CustomInput
                label="Task Title"
                placeholder="Enter your task title"
                name="title"
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.title && formik.errors.title}
                required
              />
            </div>

            <div className="amok-modal__section">
              <div className="amok-modal__field-label-row">
                <label
                  className="amok-modal__field-label"
                  htmlFor="schedule-collab-search"
                >
                  Search Collaborator
                </label>
                <span className="amok-modal__optional">(optional)</span>
              </div>
              <div className="project-collab-modal__search amok-modal__collab-search">
                <img src={APP_ICONS.messageSearchCollab} alt="" />
                <Select
                  inputId="schedule-collab-search"
                  className="project-collab-modal__user-select"
                  classNamePrefix="project-collab-modal-user"
                  options={collaboratorOptions}
                  value={formik.values.collaborators}
                  onChange={selected =>
                    formik.setFieldValue(
                      "collaborators",
                      ensureCurrentUserSelected(
                        selected,
                        currentUserOption,
                        currentUserId,
                      ),
                    )
                  }
                  onBlur={() => formik.setFieldTouched("collaborators", true)}
                  isMulti
                  isSearchable
                  isClearable={false}
                  getOptionValue={option =>
                    String(option.value || option.userId || "")
                  }
                  closeMenuOnSelect={false}
                  hideSelectedOptions
                  menuIsOpen={collabMenuOpen}
                  onMenuOpen={() => setCollabMenuOpen(true)}
                  onMenuClose={() => setCollabMenuOpen(false)}
                  placeholder="Search by email or username..."
                  noOptionsMessage={() => "No collaborators found"}
                  components={{
                    Option: CollaboratorOption,
                    MultiValueLabel: CollaboratorMultiValueLabel,
                    IndicatorSeparator: null,
                    DropdownIndicator: null,
                  }}
                  styles={collaboratorSelectStyles()}
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  menuPosition="fixed"
                  filterOption={(option, rawInput) => {
                    const query = rawInput.trim().toLowerCase()
                    if (!query) return true
                    const haystack = `${option.label} ${
                      option.data?.email || ""
                    } ${option.data?.name || ""}`.toLowerCase()
                    return haystack.includes(query)
                  }}
                  aria-label="Search Collaborator"
                />
              </div>
            </div>

            <div className="amok-modal__section">
              <div className="amok-modal__field-label-row">
                <span className="amok-modal__field-label">Task Description</span>
                <span className="amok-modal__optional">(optional)</span>
              </div>
              <CustomInput
                placeholder="Describe the task in detail"
                name="description"
                textarea
                rows={3}
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.description && formik.errors.description}
              />
            </div>

            <div className="amok-modal__section amok-modal__dates">
              <div className="amok-modal__date-field">
                <span className="amok-modal__field-label">Start Date *</span>
                <DateField
                  label="Select date"
                  value={formik.values.startDate}
                  onChange={e =>
                    formik.setFieldValue("startDate", e.target.value)
                  }
                  onBlur={() => formik.setFieldTouched("startDate", true)}
                  calendarIconSrc={ICONS.calendar}
                  disabled={isDateLocked}
                  min={datePickerMin}
                  max={datePickerMax}
                  hasError={
                    formik.touched.startDate && !!formik.errors.startDate
                  }
                />
                {formik.touched.startDate && formik.errors.startDate && (
                  <p className="amok-field__message amok-field__message--error">
                    {formik.errors.startDate}
                  </p>
                )}
              </div>
              <div className="amok-modal__date-field">
                <span className="amok-modal__field-label">End Date *</span>
                <DateField
                  label="Select date"
                  value={formik.values.endDate}
                  onChange={e =>
                    formik.setFieldValue("endDate", e.target.value)
                  }
                  onBlur={() => formik.setFieldTouched("endDate", true)}
                  calendarIconSrc={ICONS.calendar}
                  disabled={isDateLocked}
                  min={formik.values.startDate || datePickerMin}
                  max={datePickerMax}
                  hasError={formik.touched.endDate && !!formik.errors.endDate}
                />
                {formik.touched.endDate && formik.errors.endDate && (
                  <p className="amok-field__message amok-field__message--error">
                    {formik.errors.endDate}
                  </p>
                )}
              </div>
            </div>

            {hasScheduleDates || isDateLocked ? (
              <>
                <div className="amok-modal__section">
                  <div className="amok-modal__days-header">
                    <span className="amok-modal__days-title">
                      Select Working Days
                    </span>
                    <span className="amok-modal__days-hint">
                      {isDateLocked
                        ? "Hours apply to this day only"
                        : "Only days within the selected dates"}
                    </span>
                  </div>
                  <div className="amok-modal__days">
                    {DAYS.map(({ key, label }) => {
                      const checked = selectedKeys.includes(key)
                      const dayAllowed =
                        isDateLocked
                          ? key === lockedDayKey
                          : allowedWeekdays.has(key)
                      return (
                        <CustomCheckbox
                          key={key}
                          label={label}
                          checked={checked}
                          disabled={!dayAllowed}
                          onChange={() => toggleDay(key)}
                          checkIconSrc={ICONS.check}
                          className={`amok-day-chip ${
                            checked ? "amok-day-chip--selected" : ""
                          }`}
                        />
                      )
                    })}
                  </div>
                  {formik.submitCount > 0 &&
                    typeof formik.errors.workingDays === "string" && (
                      <p className="amok-field__message amok-field__message--error">
                        {formik.errors.workingDays}
                      </p>
                    )}
                </div>

                {formik.values.workingDays.length > 0 && (
                  <div className="amok-modal__table-wrapper">
                    <div className="amok-modal__table">
                      <div className="amok-modal__table-head">
                        <span>Day</span>
                        <span className="amok-modal__table-metrics amok-modal__table-metrics--head">
                          <span>Hours</span>
                          <span>Start Time</span>
                          <span>End Time</span>
                        </span>
                      </div>
                      {formik.values.workingDays.map((day, index) => {
                        const dayError =
                          formik.submitCount > 0 &&
                          Array.isArray(formik.errors.workingDays) &&
                          formik.errors.workingDays[index]
                            ? formik.errors.workingDays[index]
                            : null
                        const dayErrors = [
                          dayError?.hours,
                          dayError?.startTime,
                          dayError?.endTime,
                        ].filter(Boolean)

                        return (
                          <div className="amok-modal__table-row" key={day.day}>
                            <span className="amok-modal__table-day">
                              {dayFull(day.day)}
                            </span>
                            <div className="amok-modal__table-metrics">
                              <div className="amok-modal__hours amok-modal__hours--disabled">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.5"
                                  className="amok-modal__number"
                                  value={day.hours}
                                  disabled
                                  readOnly
                                  aria-invalid={!!dayError?.hours}
                                  aria-label={`${dayFull(day.day)} hours`}
                                />
                                <span className="amok-modal__suffix">hrs</span>
                              </div>
                              <ScheduleTimeSelect
                                id={`schedule-start-${day.day}`}
                                value={day.startTime}
                                onChange={next =>
                                  updateWorkingDay(day.day, "startTime", next)
                                }
                                hasError={!!dayError?.startTime}
                                ariaLabel={`${dayFull(day.day)} start time`}
                              />
                              <ScheduleTimeSelect
                                id={`schedule-end-${day.day}`}
                                value={day.endTime}
                                options={endTimeOptionsAfter(day.startTime)}
                                onChange={next =>
                                  updateWorkingDay(day.day, "endTime", next)
                                }
                                hasError={!!dayError?.endTime}
                                ariaLabel={`${dayFull(day.day)} end time`}
                              />
                            </div>
                            {dayErrors.length > 0 && (
                              <p className="amok-field__message amok-field__message--error amok-modal__table-row-error">
                                {dayErrors.join(". ")}
                              </p>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="amok-modal__section amok-modal__schedule-placeholder">
                <span className="amok-modal__field-label">Working Schedule</span>
                <p className="amok-modal__schedule-placeholder-text">
                  Select start and end dates first to choose working days and
                  enter hours.
                </p>
              </div>
            )}
            </div>
          </div>

          <div className="amok-modal__footer">
            <CustomButton
              variant="danger"
              type="button"
              className="amok-modal__btn"
              onClick={onClose}
              disabled={formik.isSubmitting}
            >
              Cancel
            </CustomButton>
            <CustomButton
              variant="primary"
              type="submit"
              className="amok-modal__btn"
              loading={formik.isSubmitting}
              disabled={formik.isSubmitting}
            >
              Save Schedule
            </CustomButton>
          </div>
        </form>
      </ModalBody>
    </Modal>
  )
}

SetupWorkingScheduleModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  lockedDate: PropTypes.string,
  projectStartDate: PropTypes.string,
  projectEndDate: PropTypes.string,
  project: PropTypes.object,
}

export default SetupWorkingScheduleModal
